import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { Toast, ToastTone } from "@/components/ui/Toast";

interface ToastEntry { id: number; message: string; tone?: ToastTone; }
interface Ctx { push: (t: { message: string; tone?: ToastTone }) => void; }

const ToastContext = createContext<Ctx | undefined>(undefined);
const MAX = 3;
let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const push = useCallback((t: { message: string; tone?: ToastTone }) => {
    setToasts((curr) => [...curr.slice(-(MAX - 1)), { ...t, id: nextId++ }]);
  }, []);

  // Auto-dismiss the oldest toast every 3s
  useEffect(() => {
    if (toasts.length === 0) return;
    const id = toasts[0].id;
    const timer = setTimeout(() => {
      setToasts((curr) => curr.filter((t) => t.id !== id));
    }, 3000);
    return () => clearTimeout(timer);
  }, [toasts]);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => <Toast key={t.id} message={t.message} tone={t.tone} />)}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): Ctx {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be inside ToastProvider");
  return ctx;
}
